import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, takeUntil } from 'rxjs/operators';

import { ModalDirective } from 'ngx-bootstrap/modal';

import { GridComponentBase } from 'src/app/shared/components/base/GridComponentBase';
import { AgGridUtils } from 'src/app/shared/utils/AgGridUtils';
import { clone, getCurrencyName, getValue, getYesOrNo } from 'src/app/shared/utils/Utils';
import { ClientConstants } from 'src/app/constants/client-constants';

import { ClientsService } from 'src/app/services/clients.service';
import { DialogService } from 'src/app/shared/services/dialog.service';

import { ClientDetailsModel } from 'src/app/models/clients/ClientDetailsModel';
import { RowNode } from 'ag-grid-community';
import { InvoiceRequestModel, LAST_MONTH, MONTH_TO_DATE } from 'src/app/models/projectModels/InvoiceRequestModel';

@Component({
  selector: 'trackify-clients',
  templateUrl: './clients.component.html',
  styleUrls: ['./clients.component.scss']
})
export class ClientsComponent extends GridComponentBase<ClientDetailsModel> implements OnInit {

  @ViewChild("clientModal", { static: true }) modal: ModalDirective;

  client: ClientDetailsModel;
  currencies = ClientConstants.Currencies;
  selectedRows: boolean = false;
  _selectedNodes: RowNode[] = [];
  isDropdownOpen: boolean = false;

  constructor(
    public router: Router,
    private dialogService: DialogService,
    private clientsService: ClientsService
  ) {
    super();

    AgGridUtils.addDefaultColumnTypes(this.columnTypes);

    this.addColumnDefs([
      {
        field: 'isChecked',
        width: 50,
        suppressSizeToFit: true,
        pinned: 'left',
        checkboxSelection: true,
        headerCheckboxSelection: true,
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 160, sort: 'asc',
        valueFormatter: params => getValue(params.data.name),
        suppressSizeToFit: true
      },
      {
        field: 'microsoftName',
        headerName: 'Microsoft Name',
        valueFormatter: params => getValue(params.data.microsoftName),
        width: 160,
        suppressSizeToFit: true
      },
      {
        field: 'smartBillName',
        headerName: 'SmartBill Name',
        valueFormatter: params => getValue(params.data.smartBillName),
        width: 160,
        suppressSizeToFit: true
      },
      {
        field: 'smartBillCif',
        headerName: 'SmartBill CIF',
        valueFormatter: params => getValue(params.data.smartBillCif),
        width: 140,
        suppressSizeToFit: true
      },
      {
        field: 'currency',
        headerName: 'Currency',
        valueFormatter: params => getCurrencyName(params.data.currency),
        width: 115,
        suppressSizeToFit: true
      },
      {
        field: 'tva',
        headerName: 'TVA',
        valueFormatter: params => getValue(params.data.tva),
        width: 85,
        suppressSizeToFit: true
      },
      {
        field: 'isBimonthlyBilled',
        headerName: 'Bimonthly Billed',
        valueFormatter: params => getYesOrNo(params.data.isBimonthlyBilled),
        width: 120,
        suppressSizeToFit: true
      },
      {
        field: 'generateAzureProforma',
        headerName: 'Generate Azure Proforma',
        valueFormatter: params => getYesOrNo(params.data.generateAzureProforma),
        width: 150,
        suppressSizeToFit: true
      },
      {
        field: 'generateOfficeProforma',
        headerName: 'Generate Office Proforma',
        valueFormatter: params => getYesOrNo(params.data.generateOfficeProforma),
        width: 150,
        suppressSizeToFit: true
      },
      {
        field: 'generateTimesheetProforma',
        headerName: 'Generate Timesheet Proforma',
        valueFormatter: params => getYesOrNo(params.data.generateTimesheetProforma),
        minWidth: 180,
        suppressSizeToFit: false
      },
      {
        headerName: 'Edit',
        pinned: 'right',
        headerClass: 'text-center',
        type: ['buttonColumn'],
        cellRendererParams: {
          buttonClass: 'btn btn-primary btn-xs',
          buttonText: '',
          iconClass: 'fas fa-edit white-color',
          onClick: this._onEdit.bind(this)
        },
      },
      {
        headerName: 'Archive',
        pinned: 'right',
        headerClass: 'text-center',
        type: ['buttonColumn'],
        cellRendererParams: {
          buttonClass: 'btn btn-primary btn-xs',
          buttonText: '',
          iconClass: 'fas fa-ban white-color',
          onClick: this._onArchive.bind(this)
        }
      }
    ]);

    this.gridOptions.rowSelection = 'multiple';
    this.gridOptions.overlayNoRowsTemplate = '<span class="ag-overlay-no-rows-center">No client entries.</span>';
  }

  ngOnInit(): void {
    this.startLoader();

    this.clientsService
      .getClients()
      .pipe(takeUntil(this.ngUnsubscribe), finalize(() => this.stopLoader()))
      .subscribe(clients => {
        this.data = clients;
      });
  }

  onRowSelected($event: any): void {
    if (!$event.node.isSelected()) {
      this.getSelectedRows();
      return;
    }

    this.currentSelection = clone($event.data);
    this.client = clone(this.currentSelection);
    this.currentSelectionIdx = this.data.indexOf(this.data.find(u => u.id == this.currentSelection.id));

    this.getSelectedRows();
  }

  onRowDoubleClicked(_: any): void {
    this._onEdit();
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  getInvoiceForLastMonth(): void {
    this._invoicesConfirmation(LAST_MONTH);
  }

  getInvoiceForMonthToDate(): void {
    this._invoicesConfirmation(MONTH_TO_DATE);
  }

  validateModel(_: string): string { return ''; }

  isModelValid(): boolean { return true; }

  onAdd(): void {
    this.client = new ClientDetailsModel();
    this.modal.show();
  }

  saveChanges(): void {
    if (this.client.id) {
      this._editClient();
    }
    else {
      this._addClient();
    }
  }

  onSelectionChanged(): void {
    this._selectedNodes = this._getRowsAfterFilter();
    if (this._selectedNodes.length != 1) {
      return;
    }

    this.selectedRows = true;
    this.currentSelection = this._selectedNodes[0].data;
  }

  getSelectedRows(): void {
    this._selectedNodes = this._getRowsAfterFilter();

    if (this._selectedNodes.length == 0 || this._selectedNodes.length > 1) {
      this.currentSelection = null;
    }

    this.selectedRows = !!this._selectedNodes.length;
  }

  private _getRowsAfterFilter(): RowNode[] {
    this._selectedNodes = [];
    this.gridOptions.api.forEachNodeAfterFilter(node => {
      if (node.isSelected()) {
        this.isDropdownOpen = false;
        this._selectedNodes.push(node);
      }
    });

    return this._selectedNodes;
  }

  private _onEdit(): void {
    this.client = clone(this.currentSelection);
    this.modal.show();
  }

  private _onArchive(): void {
    this.dialogService.showDialog({
      title: `Are you sure you want to archive this client?`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC3545',
      confirmButtonText: 'Archive'
    }).then((result) => {
      if (!result.value) return;
      this._archiveClient();
    });
  }

  private _addClient(): void {
    this.clientsService
      .addClient(this.client)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(updatedClient => {
        this.updateGridAfterAdd(updatedClient);
        this.modal.hide();
        this.dialogService.showSuccessMessage('Success!', 'The client was added successfully.');
      }, err => { this.dialogService.showSimpleDialog("Error", err, "error"); });
  }

  private _editClient(): void {
    this.clientsService
      .editClient(this.client)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(updatedClient => {
        this.updateGridAfterEdit(updatedClient);
        this.currentSelection = this.client;
        this.modal.hide();
        this.dialogService.showSuccessMessage('Success!', 'The client was updated successfully.');
      }, err => { this.dialogService.showSimpleDialog("Error", err, "error"); });
  }

  private _archiveClient(): void {
    this.clientsService
      .archiveClient(this.currentSelection.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(_ => {
        this.data.splice(this.currentSelectionIdx, 1);
        this.updateGridAfterDelete();
        this.currentSelection = null;
        this.client = null;
        this.dialogService.showSuccessMessage('Success!', 'The client was archived successfully.');
      }, err => { this.dialogService.showSimpleDialog("Error", err, "error"); });
  }

  private _invoicesConfirmation(month: number): void {
    let selectionData = this._selectedNodes.map(element => element.data);
    let clientsName = selectionData.map(client => client.name);

    if(clientsName.length > 8){
      clientsName = clientsName.slice(0,8);
      console.log(clientsName);
      clientsName.push('etc.');
    }

    this.dialogService.showDialog({
      title: `Are you sure you want to generate invoices for the following clients: ${clientsName.join(', ')}?`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC3545',
      confirmButtonText: 'Generate',
      customClass: 'dynamic-font-size-dialog'
    }).then((result) => {
      if (!result.value) return;
      this.gridOptions.api.showLoadingOverlay();

      let model = new InvoiceRequestModel();
      model.ids = selectionData.map(client => client.id);
      model.month = month;

      this._generateInvoices(model);
    });
  }

  private _generateInvoices(model: InvoiceRequestModel): void {
    this.clientsService.generateInvoices(model)
      .pipe(takeUntil(this.ngUnsubscribe), finalize(() => {
        this.gridOptions.api.deselectAll();
        this.gridOptions.api.hideOverlay();
      }))
      .subscribe(_ => {
        this.getSelectedRows();
        this.dialogService.showSuccessMessage('Success!', 'Invoice Generated');
      },
        err => {
          this.dialogService.showSimpleDialog("Error", err, "error");
        })
  }
}